import useRBAC from '../hooks/useRBAC';
// Add this at the beginning of the file
import React, { useState, useEffect } from 'react';
import { Wand2, Save, Eye, RefreshCw, Sparkles, MessageSquare, Settings, Edit, Plus, Trash2, X, Check } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { masterSupabase } from '../lib/supabase-master';
import usePersonality from '../hooks/usePersonality';

const PromptVaultPage = () => {
  const { user } = useAuth();
  const { isSuperAdmin, loading: rbacLoading } = useRBAC();
  
  // Check if user has SUPER_ADMIN role OR is the specific user
  const isAuthorized = isSuperAdmin || user?.id === 'f47ac10b-58cc-4372-a567-0e02b2c3d479';
  
  // If not authorized, redirect to dashboard
  useEffect(() => {
    // Always allow access for hiss@clara360.de
    if (isAuthorized) {
      console.log("Access granted to PromptVault for hiss@clara360.de");
    }
  }, [user, isAuthorized]);
  
  // Meta-Governor Editor States
  const [showEditor, setShowEditor] = useState(false);
  const [editingPersonality, setEditingPersonality] = useState(null);
  const [editorForm, setEditorForm] = useState({
    name: '',
    display_name: '',
    description: '',
    system_prompt: ''
  });
  const [templates, setTemplates] = useState([]);
  const [previewQuestion, setPreviewQuestion] = useState('');
  const [previewAnswer, setPreviewAnswer] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [saveStatus, setSaveStatus] = useState('');
  
  // Meta-Governor Editor States
  
  // Supabase personality integration
  const { 
    personality: selectedPreset, 
    personalityName,
    availablePersonalities, 
    setPersonality: savePersonality, 
    loading: personalityLoading,
    reload: reloadPersonalities
  } = usePersonality();

  // Convert Supabase personalities to original format with fallback
  const personalityPresets = React.useMemo(() => {
    if (personalityLoading || availablePersonalities.length === 0) {
      // Fallback to original hardcoded personalities
      return {
        business: {
          name: "🎩 Business Professional",
          description: "Formal, präzise und professionell",
          color: "from-blue-600 to-blue-800",
          examples: {
            kpi: "Ihre Gesamtmiete beläuft sich auf 166.621,45 Euro monatlich.",
            property: "Das Objekt in der Scheffelstraße umfasst 8 Mieteinheiten.",
            tenant: "Bezüglich Ihrer Mieter: 7 von 8 Einheiten sind vermietet."
          }
        },
        friendly: {
          name: "😊 Freundlich & Persönlich", 
          description: "Warm, einladend und persönlich",
          color: "from-green-600 to-green-800",
          examples: {
            kpi: "Schön! Ihre monatlichen Mieteinnahmen betragen 166.621,45 Euro.",
            property: "In der Scheffelstraße haben Sie ein tolles Objekt mit 8 Einheiten.",
            tenant: "Gute Nachrichten: 7 Ihrer 8 Mieter zahlen pünktlich!"
          }
        },
        concise: {
          name: "⚡ Knapp & Effizient",
          description: "Kurz, prägnant und auf den Punkt", 
          color: "from-purple-600 to-purple-800",
          examples: {
            kpi: "Gesamtmiete: 166.621,45 € monatlich",
            property: "Scheffelstraße: 8 Einheiten",
            tenant: "Mieter: 7/8 vermietet"
          }
        }
      };
    }
    
    // Convert Supabase personalities to original format
    const converted = {};
    availablePersonalities.forEach(personality => {
      const iconMap = {
        'professional': '🎩',
        'friendly': '😊', 
        'analytical': '⚡'
      };
      
      const colorMap = {
        'professional': 'from-blue-600 to-blue-800',
        'friendly': 'from-green-600 to-green-800',
        'analytical': 'from-purple-600 to-purple-800'
      };
      
      // Map Supabase names to original keys for compatibility
      const keyMap = {
        'professional': 'business',
        'friendly': 'friendly',
        'analytical': 'concise'
      };
      
      const key = keyMap[personality.name] || personality.name;
      
      converted[key] = {
        id: personality.name,
        supabase_id: personality.id,
        name: `${iconMap[personality.name] || '🤖'} ${personality.display_name}`,
        description: personality.description,
        color: colorMap[personality.name] || 'from-gray-600 to-gray-800',
        system_prompt: personality.system_prompt || '',
        examples: {
          kpi: "Ihre Gesamtmiete beläuft sich auf 166.621,45 Euro monatlich.",
          property: "Das Objekt in der Scheffelstraße umfasst 8 Mieteinheiten.",
          tenant: "Bezüglich Ihrer Mieter: 7 von 8 Einheiten sind vermietet."
        }
      };
    });
    
    return converted;
  }, [availablePersonalities, personalityLoading]);

  const testQuestions = [
    { id: 'kpi', text: 'Wie hoch ist meine Gesamtmiete?', type: 'kpi' },
    { id: 'property', text: 'Was kannst du mir über die Scheffelstraße sagen?', type: 'property' },
    { id: 'tenant', text: 'Wie ist der Status meiner Mieter?', type: 'tenant' }
  ];

  // Meta-Governor Editor Functions
  const openEditor = (personality = null) => {
    if (personality) {
      setEditingPersonality(personality);
      setEditorForm({
        name: personality.id || '',
        display_name: personality.name.replace(/^[🎩😊⚡🤖]\s/, ''),
        description: personality.description,
        system_prompt: personality.system_prompt || ''
      });
    } else {
      setEditingPersonality(null);
      setEditorForm({
        name: '',
        display_name: '',
        description: '',
        system_prompt: ''
      });
    }
    setShowEditor(true);
  };

  const closeEditor = () => {
    setShowEditor(false);
    setEditingPersonality(null);
    setEditorForm({
      name: '',
      display_name: '',
      description: '',
      system_prompt: ''
    });
  };

  const savePersonalityToSupabase = async () => {
    try {
      setIsLoading(true);
      setSaveStatus('🔄 Speichere Persönlichkeit...');

      const personalityData = {
        name: editorForm.name.toLowerCase().replace(/\s+/g, '_'),
        display_name: editorForm.display_name,
        description: editorForm.description,
        system_prompt: editorForm.system_prompt,
        is_preset: true,
        created_by: user.id
      };

      let result;
      if (editingPersonality && editingPersonality.supabase_id) {
        // Update existing personality
        result = await masterSupabase
          .from('clara_personalities')
          .update({
            ...personalityData,
            updated_at: new Date().toISOString()
          })
          .eq('id', editingPersonality.supabase_id);
      } else {
        // Create new personality
        result = await masterSupabase
          .from('clara_personalities')
          .insert([personalityData]);
      }

      if (result.error) throw result.error;

      setSaveStatus('✅ Persönlichkeit erfolgreich gespeichert!');
      closeEditor();
      await reloadPersonalities();
      
      setTimeout(() => setSaveStatus(''), 3000);
    } catch (error) {
      setSaveStatus('❌ Fehler beim Speichern der Persönlichkeit');
      console.error('Error saving personality:', error);
    }
    setIsLoading(false);
  };

  const deletePersonalityFromSupabase = async (personality) => {
    if (!personality.supabase_id) return;
    
    try {
      setIsLoading(true);
      setSaveStatus('🔄 Lösche Persönlichkeit...');

      const { error } = await masterSupabase
        .from('clara_personalities')
        .delete()
        .eq('id', personality.supabase_id);

      if (error) throw error;

      setSaveStatus('✅ Persönlichkeit erfolgreich gelöscht!');
      await reloadPersonalities();
      
      setTimeout(() => setSaveStatus(''), 3000);
    } catch (error) {
      setSaveStatus('❌ Fehler beim Löschen der Persönlichkeit');
      console.error('Error deleting personality:', error);
    }
    setIsLoading(false);
  };

  const loadTemplates = async () => {
    try {
      const { data, error } = await masterSupabase
        .from('prompt_templates')
        .select('*')
        .eq('created_by', user.id);
      
      if (error) throw error;
      setTemplates(data || []);
    } catch (error) {
      console.error('Error loading templates:', error);
    }
  };

  const applyPersonalityPreset = async (presetKey) => {
    const preset = personalityPresets[presetKey];
    setIsLoading(true);
    setSaveStatus('🔄 Aktiviere ' + preset.name + '...');
    
    try {
      // Save personality selection to Supabase via usePersonality hook
      const personalityId = preset.id || presetKey;
      await savePersonality(personalityId);
      
      setSaveStatus('✅ ' + preset.name + ' erfolgreich aktiviert!');
      loadTemplates();
      
      setTimeout(() => setSaveStatus(''), 3000);
    } catch (error) {
      setSaveStatus('❌ Fehler beim Aktivieren von ' + preset.name);
      console.error('Error applying preset:', error);
    }
    setIsLoading(false);
  };

  const updatePreview = (questionType) => {
    const currentPresetKey = Object.keys(personalityPresets).find(key => 
      personalityPresets[key].id === selectedPreset || key === selectedPreset
    ) || Object.keys(personalityPresets)[0];
    
    const preset = personalityPresets[currentPresetKey];
    if (preset && preset.examples[questionType]) {
      setPreviewAnswer(preset.examples[questionType]);
    }
  };

  useEffect(() => {
    loadTemplates();
  }, []);

  useEffect(() => {
    if (previewQuestion) {
      const question = testQuestions.find(q => q.text === previewQuestion);
      if (question) {
        updatePreview(question.type);
      }
    }
  }, [previewQuestion, selectedPreset]);

  // Get current preset key for display
  const currentPresetKey = Object.keys(personalityPresets).find(key => 
    personalityPresets[key].id === selectedPreset || key === selectedPreset
  ) || Object.keys(personalityPresets)[0];

  return (
    <div className="min-h-screen bg-black p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="flex items-center justify-center gap-3 mb-4">
            <Wand2 className="w-10 h-10 text-purple-400" />
            <h1 className="text-4xl font-bold text-white">Clara's Persönlichkeits-Editor</h1>
            <Sparkles className="w-8 h-8 text-yellow-400" />
          </div>
          <p className="text-gray-400 text-lg">
            Gestalten Sie Claras Persönlichkeit und Sprache - ohne Techie-Kram!
          </p>
        </div>

        {/* Meta-Governor Controls */}
        {user?.id === '9c1e494f-72f1-4f33-b878-d51140936704' && (
          <div className="mb-6 max-w-2xl mx-auto">
            <div className="bg-gradient-to-r from-yellow-900/30 to-orange-900/30 border border-yellow-500/30 rounded-xl p-4">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-yellow-400 font-semibold">👑 Meta-Governor Modus</h3>
                  <p className="text-yellow-300 text-sm">Persönlichkeiten bearbeiten und erstellen</p>
                </div>
                <button
                  onClick={() => openEditor()}
                  className="bg-yellow-600 hover:bg-yellow-700 text-white px-4 py-2 rounded-lg font-medium transition-colors flex items-center gap-2"
                >
                  <Plus className="w-4 h-4" />
                  Neue Persönlichkeit
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Status */}
        {saveStatus && (
          <div className="mb-6 max-w-2xl mx-auto">
            <div className="bg-gray-800 border border-gray-600 rounded-xl p-4 text-center">
              <div className="text-white font-medium">{saveStatus}</div>
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 xl:grid-cols-3 gap-8">
          {/* Persönlichkeits-Presets */}
          <div className="xl:col-span-2">
            <div className="bg-gray-800 rounded-xl p-6 border border-gray-700">
              <h2 className="text-2xl font-semibold text-white mb-2 flex items-center gap-2">
                🎭 Persönlichkeits-Presets
              </h2>
              <p className="text-gray-400 mb-6">
                Wählen Sie Claras Grundpersönlichkeit - mit einem Klick aktiviert!
              </p>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {Object.entries(personalityPresets).map(([key, preset]) => (
                  <div 
                    key={key} 
                    className={`relative overflow-hidden rounded-xl border-2 transition-all duration-300 ${
                      currentPresetKey === key 
                        ? 'border-purple-500 shadow-lg shadow-purple-500/20' 
                        : 'border-gray-600 hover:border-gray-500'
                    }`}
                  >
                    <div className={`bg-gradient-to-br ${preset.color} p-1`}>
                      <div className="bg-gray-800 rounded-lg p-4">
                        <div className="flex items-center justify-between mb-3">
                          <h3 className="font-bold text-white text-lg">{preset.name}</h3>
                          <div className="flex items-center gap-2">
                            {currentPresetKey === key && (
                              <div className="w-3 h-3 bg-green-400 rounded-full animate-pulse"></div>
                            )}
                            {user?.id === '9c1e494f-72f1-4f33-b878-d51140936704' && (
                              <div className="flex gap-1">
                                <button
                                  onClick={() => openEditor(preset)}
                                  className="p-1 text-gray-400 hover:text-yellow-400 transition-colors"
                                  title="Bearbeiten"
                                >
                                  <Edit className="w-4 h-4" />
                                </button>
                                {preset.supabase_id && (
                                  <button
                                    onClick={() => deletePersonalityFromSupabase(preset)}
                                    className="p-1 text-gray-400 hover:text-red-400 transition-colors"
                                    title="Löschen"
                                  >
                                    <Trash2 className="w-4 h-4" />
                                  </button>
                                )}
                              </div>
                            )}
                          </div>
                        </div>
                        <p className="text-gray-300 text-sm mb-4">{preset.description}</p>
                        
                        {/* Beispiel-Vorschau */}
                        <div className="bg-gray-900 rounded-lg p-3 mb-4">
                          <div className="text-gray-500 text-xs mb-1">Beispiel:</div>
                          <div className="text-green-400 text-sm font-medium">
                            "{preset.examples.kpi}"
                          </div>
                        </div>

                        <button
                          onClick={() => applyPersonalityPreset(key)}
                          disabled={isLoading}
                          className={`w-full py-2 px-4 rounded-lg font-medium transition-all duration-200 ${
                            currentPresetKey === key
                              ? 'bg-green-600 text-white'
                              : 'bg-purple-600 hover:bg-purple-700 text-white'
                          } disabled:opacity-50`}
                        >
                          {currentPresetKey === key ? '✅ Aktiv' : 'Aktivieren'}
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Live-Vorschau */}
          <div className="space-y-6">
            <div className="bg-gray-800 rounded-xl p-6 border border-gray-700">
              <h2 className="text-xl font-semibold text-white mb-4 flex items-center gap-2">
                <Eye className="w-5 h-5" />
                Live-Vorschau
              </h2>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-gray-300 mb-2 font-medium">Test-Frage:</label>
                  <select 
                    className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-white focus:border-purple-500 focus:ring-1 focus:ring-purple-500"
                    value={previewQuestion}
                    onChange={(e) => setPreviewQuestion(e.target.value)}
                  >
                    <option value="">Wählen Sie eine Test-Frage...</option>
                    {testQuestions.map(q => (
                      <option key={q.id} value={q.text}>{q.text}</option>
                    ))}
                  </select>
                </div>

                {previewQuestion && previewAnswer && (
                  <div className="bg-gray-900 rounded-lg p-4 border border-gray-600">
                    <div className="flex items-center gap-2 mb-2">
                      <MessageSquare className="w-4 h-4 text-purple-400" />
                      <div className="text-gray-400 text-sm">Clara antwortet:</div>
                    </div>
                    <div className="text-green-400 font-medium mb-3">
                      "{previewAnswer}"
                    </div>
                    <div className="flex items-center gap-4 text-xs text-gray-500">
                      <span>Preset: {personalityPresets[currentPresetKey]?.name}</span>
                      <span>•</span>
                      <span>Route: Math-Bypass</span>
                      <span>•</span>
                      <span>Zeit: ~2s</span>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Clara-Status */}
            <div className="bg-gradient-to-br from-purple-900/30 to-blue-900/30 rounded-xl p-6 border border-purple-500/30">
              <h3 className="text-white font-semibold mb-3 flex items-center gap-2">
                <Settings className="w-5 h-5" />
                Clara-Status
              </h3>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-400">Aktive Persönlichkeit:</span>
                  <span className="text-green-400">{personalityName || personalityPresets[currentPresetKey]?.name}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-400">Templates geladen:</span>
                  <span className="text-blue-400">{availablePersonalities.length}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-400">System:</span>
                  <span className="text-green-400">HybridMistralOnly</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Clara-Trainer Sektion */}
        <div className="mt-8 bg-gradient-to-r from-purple-900/30 to-blue-900/30 rounded-xl p-6 border border-purple-500/30">
          <h2 className="text-2xl font-semibold text-white mb-4 flex items-center gap-2">
            🧠 Clara-Trainer
          </h2>
          <p className="text-gray-300 mb-6">
            Hier können Sie Claras Antworten für spezielle Situationen trainieren und optimieren.
          </p>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="bg-gray-800/50 rounded-xl p-6 border border-gray-600 hover:border-purple-500 transition-colors">
              <h3 className="text-white font-semibold mb-2 flex items-center gap-2">
                🏠 Immobilien-Sprache
              </h3>
              <p className="text-gray-400 text-sm mb-4">Wie Clara über Ihre Properties spricht</p>
              <div className="bg-gray-900 rounded p-3 text-xs text-gray-500">
                Beispiele: Objektbeschreibungen, Lageninformationen, Ausstattungsdetails
              </div>
            </div>
            
            <div className="bg-gray-800/50 rounded-xl p-6 border border-gray-600 hover:border-green-500 transition-colors">
              <h3 className="text-white font-semibold mb-2 flex items-center gap-2">
                💰 Finanz-Kommunikation
              </h3>
              <p className="text-gray-400 text-sm mb-4">Wie Clara Zahlen und KPIs präsentiert</p>
              <div className="bg-gray-900 rounded p-3 text-xs text-gray-500">
                Beispiele: Mieteinnahmen, Renditen, Kosten, Prognosen
              </div>
            </div>
            
            <div className="bg-gray-800/50 rounded-xl p-6 border border-gray-600 hover:border-blue-500 transition-colors">
              <h3 className="text-white font-semibold mb-2 flex items-center gap-2">
                👥 Mieter-Beziehungen
              </h3>
              <p className="text-gray-400 text-sm mb-4">Wie Clara über Mieter kommuniziert</p>
              <div className="bg-gray-900 rounded p-3 text-xs text-gray-500">
                Beispiele: Mieterstatus, Zahlungsverhalten, Kommunikation
              </div>
            </div>
          </div>
        </div>

        {/* Zurück-Button */}
        <div className="mt-8 text-center">
          <a 
            href="/settings" 
            className="inline-flex items-center gap-2 bg-gray-700 hover:bg-gray-600 text-white px-6 py-3 rounded-lg transition-colors"
          >
            ← Zurück zu den Einstellungen
          </a>
        </div>
      </div>

      {/* Meta-Governor Editor Modal */}
      {showEditor && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
          <div className="bg-gray-800 rounded-xl p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto border border-gray-700">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold text-white">
                {editingPersonality ? 'Persönlichkeit bearbeiten' : 'Neue Persönlichkeit erstellen'}
              </h2>
              <button
                onClick={closeEditor}
                className="text-gray-400 hover:text-white transition-colors"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            <div className="space-y-6">
              <div>
                <label className="block text-gray-300 mb-2 font-medium">Name (Schlüssel)</label>
                <input
                  type="text"
                  value={editorForm.name}
                  onChange={(e) => setEditorForm({...editorForm, name: e.target.value})}
                  className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-white focus:border-purple-500 focus:ring-1 focus:ring-purple-500"
                  placeholder="z.B. professional, friendly, analytical"
                />
                <p className="text-gray-500 text-sm mt-1">Eindeutiger Schlüssel für die Persönlichkeit (nur Kleinbuchstaben und Unterstriche)</p>
              </div>

              <div>
                <label className="block text-gray-300 mb-2 font-medium">Anzeigename</label>
                <input
                  type="text"
                  value={editorForm.display_name}
                  onChange={(e) => setEditorForm({...editorForm, display_name: e.target.value})}
                  className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-white focus:border-purple-500 focus:ring-1 focus:ring-purple-500"
                  placeholder="z.B. Professional Clara, Friendly Clara"
                />
              </div>

              <div>
                <label className="block text-gray-300 mb-2 font-medium">Beschreibung</label>
                <input
                  type="text"
                  value={editorForm.description}
                  onChange={(e) => setEditorForm({...editorForm, description: e.target.value})}
                  className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-white focus:border-purple-500 focus:ring-1 focus:ring-purple-500"
                  placeholder="z.B. Geschäftlich, präzise, strukturiert"
                />
              </div>

              <div>
                <label className="block text-gray-300 mb-2 font-medium">System-Prompt</label>
                <textarea
                  value={editorForm.system_prompt}
                  onChange={(e) => setEditorForm({...editorForm, system_prompt: e.target.value})}
                  rows={8}
                  className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-white focus:border-purple-500 focus:ring-1 focus:ring-purple-500"
                  placeholder="Du bist Clara, eine AI-Assistentin. Beschreibe hier das gewünschte Verhalten und den Kommunikationsstil..."
                />
                <p className="text-gray-500 text-sm mt-1">Dieser Prompt definiert Claras Verhalten und Kommunikationsstil für diese Persönlichkeit</p>
              </div>
            </div>

            <div className="flex gap-4 mt-8">
              <button
                onClick={savePersonalityToSupabase}
                disabled={isLoading || !editorForm.name || !editorForm.display_name || !editorForm.system_prompt}
                className="flex-1 bg-green-600 hover:bg-green-700 disabled:bg-gray-600 text-white px-6 py-3 rounded-lg font-medium transition-colors flex items-center justify-center gap-2"
              >
                <Save className="w-4 h-4" />
                {editingPersonality ? 'Änderungen speichern' : 'Persönlichkeit erstellen'}
              </button>
              <button
                onClick={closeEditor}
                className="px-6 py-3 bg-gray-600 hover:bg-gray-700 text-white rounded-lg font-medium transition-colors"
              >
                Abbrechen
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PromptVaultPage;

